import LinkableMixin from './mixins/LinkableMixin';
import Element from '../ui/Element';
import mixin from '../privates/utils/mixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import DisabledMixin from './mixins/DisabledMixin';
import ClickableMixin from './mixins/ClickableMixin';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import LabelMixin from './mixins/LabelMixin';

/**
 * @class Button
 * @summary A text button or an icon button.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.LinkableMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.StyleMixin
 * @mixes $w.LabelMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.SiteButton
 * @viewername wysiwyg.common.components.imagebutton.viewer.ImageButton
 * @definitionId wysiwyg.viewer.components.SiteButton
 * @definitionId wysiwyg.viewer.components.PopupCloseTextButton
 * @definitionId wysiwyg.viewer.components.PopupCloseIconButton
 * @definitionId wysiwyg.common.components.imagebutton.viewer.ImageButton
 * @definitionId wysiwyg.viewer.components.SiteButton
 * @tagname button
 */
class Button extends Element {

    toJSON() {
        const {id, type, global, rendered, label} = this;
        return {id, type, global, rendered, label};
    }

    //DESCRIPTION OVERRIDES
    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the button's styles.
     * @description
     * The following styles can be used with buttons:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`borderRadius`]($w.Style.html#borderRadius)
     *  + [`borderWidth`]($w.Style.html#borderWidth)
     *  + [`color`]($w.Style.html#color)
     *
     *
     *  Getting or setting a button's styles, gets or sets the styles of the
     *  button's regular state. It does not set the styles of the button's hover
     *  or disabled states.
     *
     *  > **Note:** Not all styles can be used on all button designs. To determine which styles
     *  work with a specific button design, go to the **Button Design** panel in
     *  the Editor, choose a design, and click **Customize Design**. The design
     *  options that you see in the **Button Design** panel for your particular
     *  button are the styles you can use in your code.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.Button
     * @readonly
     * @override
     */

    /**
     * @member label
     * @label property
     * @syntax
     * get label(): string
     * set label(value: string): void
     * @summary Sets or gets the label of a text button.
     * @type {external:String}
     * @snippet [Button-label_get.es6=Get a button's label]
     * @snippet [Button-label_set.es6=Set a button's label]
     * @default Empty string
     * @memberof $w.Button
     * @override
     */

    /**
     * @member label
     * @memberof $w.Button
     * @param {external:String} value
     */

    /**
     * @member link
     * @label property
     * @syntax
     * get link(): string
     * set link(value: string): void
     * @summary Sets or gets the button's link.
     * @description
     *  When the button is clicked, the browser navigates to the `link` location.
     *
     *  The different types of links you can use are:
     *
     *  + `/localPageURL`: another page on your site
     *  + `/`: your site's home page
     *  + `http(s)://<url>`: an external web address
     *  + `wix:document://<location>`: a document stored in the Media Manager
     *  + `mailto:<email-address>?subject=<subject>`: an email
     *  + `tel:<phone number>`: a phone number
     *
     *
     *  The `link` property attempts to properly encode the URL value that
     *  you set. For example, `.../some page` is encoded to `.../some%20page`.
     *  However, some URLs do not have one unambiguous encoding. In those cases
     *  it is up to you to encode the URL to reflect your intentions. Because of
     *  these situations, it is a best practice to always encode URLs before you
     *  set them as the value of the `link` property. Regardless of how you set
     *  the `link` property, when you get its value, it returns an encoded URL.
     *
     *  Note that Wix URLs do not contain spaces. A page which has spaces in its
     *  name has its spaces replaced with dashes (`-`). Similarly,
     *  a [dynamic page URL](https://support.wix.com/en/article/creating-a-unique-dynamic-page-url) which 
     *  contains the value of a field in your collection with spaces
     *  has its spaces replaced with dashes (`-`).
     * 
     * > **Note:** Make sure to set your button link before running (and not inside) the [`onClick()`](https://www.wix.com/velo/reference/$w/button/onclick) event handler.
     * @type {String}
     * @default An empty string
     * @snippet [LinkableMixin-link_get.es6=Get the link URL from an element]
     * @snippet [LinkableMixin-link_set_local.es6=Set an element to open a local link]
     * @snippet [LinkableMixin-link_set_weblink.es6=Set an element to open an external web link]
     * @snippet [LinkableMixin-link_set_weblink_new_page.es6=Set an element to open an external web link in a new browser page when clicked]
     * @snippet [LinkableMixin-link_set_mailto.es6=Set an element to open a new email window when clicked]
     * @snippet [LinkableMixin-link_set_document.es6=Set an element to open a document]
     * @snippet [LinkableMixin-link_set_telephone.es6=Set an element to make a phone call when clicked]
     * @see [target](#target)
     * @memberof $w.Button
     * @override
     */

    /**
     * @member link
     * @param {String} value
     * @memberof $w.Button
     */

}

mixin(Button.prototype, LinkableMixin.prototype);
mixin(Button.prototype, HiddenCollapsedMixin.prototype);
mixin(Button.prototype, DisabledMixin.prototype);
mixin(Button.prototype, ClickableMixin.prototype);
mixin(Button.prototype, StyleMixin.prototype);
mixin(Button.prototype, LabelMixin.prototype);

export default Button;
